import React, { ReactElement, useEffect, useState, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';

import QuestionDefinition from './QuestionDefinition';
import QuestionDetails from './QuestionDetails';
import {
  getQuestionTypeDetails,
  getPersonAssociation,
  getQuestionOptions,
} from './formatQuestionData';

import { SlidePanel, Tabs } from 'ui/patterns';
import { Button } from 'ui/components';

import { useGetUrlValidation } from 'hooks';

import { showErrorToast } from 'utils/showToast';
import {
  checkKeyForNumericString,
  checkifEmptyItemName,
} from 'utils/choiceTypeValidations';

import {
  MappedColumnArray,
  TemplateType,
  QuestionPropsType,
  DependencyType,
} from './questionTypes';
import { SectionTypeInput } from 'types/forms';
import { useValidateLimitedAccessChoice } from 'hooks/useLimitedAccessChoiceValidation';
import { confirmAlert } from 'utils/confirm-alert';

export default function AddEditQuestion({
  showPanel,
  togglePanel,
  handleAddEditQuestion,
  currentSection,
  selectedQuestionType,
  setQuestionType,
  currentQuestion,
  accountId,
  accountInfoReturn,
  sections,
  handleSaveForm,
  url,
  setUrl,
}: QuestionPropsType): ReactElement {
  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    errors,
    getValues,
  } = useForm();

  // look up question state and functions
  const [mappedColumns, setMappedColumns] = useState<MappedColumnArray>([]);
  const [lookupDependency, setLookupDependency] = useState<DependencyType>([]);
  const [submit, setSubmit] = useState<boolean>(false);
  const [isLookupFetching, setIsLookupFetching] = useState(false);
  // choice type question state and function
  const [questionTemplateOptions, setQuestionTemplateOptions] =
    useState<TemplateType>([]);

  const {
    data: validatedResult,
    refetch: geUrlValidation,
    isFetching,
  } = useGetUrlValidation(url);

  const mutation = useValidateLimitedAccessChoice();

  useEffect(() => {
    if (selectedQuestionType?.QuestionTypeOption?.Dependencies)
      setLookupDependency(
        selectedQuestionType?.QuestionTypeOption?.Dependencies,
      );
  }, [selectedQuestionType]);

  useEffect(() => {
    if (selectedQuestionType?.QuestionTypeOption?.MappedLookupColumns) {
      setMappedColumns(
        selectedQuestionType?.QuestionTypeOption?.MappedLookupColumns,
      );
    }
  }, [selectedQuestionType]);

  useEffect(() => {
    if (selectedQuestionType?.QuestionTemplateOptions)
      setQuestionTemplateOptions(
        selectedQuestionType?.QuestionTemplateOptions.sort(
          (
            a: { Order: number; ItemLabel: string },
            b: { Order: number; ItemLabel: string },
          ) => {
            if (parseInt(`${a.Order}`) === parseInt(`${b.Order}`))
              return `${a.ItemLabel}`.toLowerCase() <
                `${b.ItemLabel}`.toLowerCase()
                ? -1
                : 1;
            return parseInt(`${a.Order}`) - parseInt(`${b.Order}`);
          },
        ).map((item: any) => {
          return {
            ...item,
            QuestionTemplateOptionId: item.QuestionTemplateOptionId
              ? item.QuestionTemplateOptionId
              : item.Id,
            DependentSections: item.DependentSections
              ? item.DependentSections
              : [],
            DependentQuestions: item.DependentQuestions
              ? item.DependentQuestions
              : [],
          };
        }),
      );
  }, [selectedQuestionType]);

  function normalizeBoolean(value: any, defaultValue = false): boolean {
    if (value === true || value === 'true' || value === 1) return true;
    if (value === false || value === 'false' || value === 0) return false;

    // Special case: sometimes RHF or API returns [] by mistake
    if (Array.isArray(value)) return defaultValue;

    if (value === undefined || value === null || value === '') {
      return defaultValue;
    }

    return defaultValue;
  }

  useEffect(() => {
    if (
      selectedQuestionType &&
      Object.keys(selectedQuestionType).length === 0 &&
      showPanel == false
    )
      return;
    const timer = setTimeout(() => {
      // if (selectedQuestionType) {
      // let personAssociationId = -1;
      // if (
      //   selectedQuestionType?.PersonAssociation === null ||
      //   selectedQuestionType?.PersonAssociation === -1
      // )
      //   personAssociationId = 0;

      // if (selectedQuestionType?.PersonAssociation === 0)
      //   personAssociationId = 1;
      // if (selectedQuestionType?.PersonAssociation === 1)
      //   personAssociationId = 2;
      reset({
        ...selectedQuestionType,
        ...selectedQuestionType?.QuestionTypeOption,
        PersonAssociation: selectedQuestionType?.PersonAssociation ?? 0,
        UpdatedFieldLabel: selectedQuestionType?.FieldLabel,
        Type: selectedQuestionType?.QuestionTypeOption?.Type === 0 ? 0 : 1,
        CustomLookupId:
          selectedQuestionType?.QuestionTypeOption?.CustomLookupId,
        QuestionDetailType: selectedQuestionType?.QuestionDetail?.Type,
        ExternalDocumentUri:
          selectedQuestionType?.QuestionDetail?.ExternalDocumentUri,
        HelpText: selectedQuestionType?.QuestionDetail?.HelpText,
        ProcedureUri: selectedQuestionType?.QuestionDetail?.ProcedureUri,
        ChoiceType: selectedQuestionType?.QuestionTypeOption?.Type,
        IsRequired: selectedQuestionType?.QuestionTypeOption?.IsRequired,
        IsNeverSentToAccount: normalizeBoolean(
          selectedQuestionType?.QuestionTypeOption?.IsNeverSentToAccount,
        ),
        IsNotSentToAccountIfNoAnswer: normalizeBoolean(
          selectedQuestionType?.QuestionTypeOption
            ?.IsNotSentToAccountIfNoAnswer,
        ),
        OriginalFieldLabel: selectedQuestionType?.QuestionTemplate?.FieldLabel,
      });

      // }
    }, 0);
    return () => clearTimeout(timer);
  }, [selectedQuestionType, reset]);

  const errorMessage = useMemo(() => {
    if (validatedResult) {
      if (validatedResult?.Data?.StatusCode === 200 && submit && !isFetching)
        return 'valid';
      if (validatedResult?.Data?.StatusCode == null && submit && !isFetching)
        return 'invalid';
    }
    return '';
  }, [validatedResult, submit, isFetching]);

  function resetForm(): void {
    setQuestionType({});
    setLookupDependency([]);
    reset({
      IsRequired: false,
    });
  }

  const associatedPersons = [
    { id: 0, name: 'None' },
    { id: 1, name: 'Caller' },
    { id: 2, name: 'Person of concern' },
  ];

  function onSubmit(
    data: any,
    cb?: (newSections: Array<SectionTypeInput>, questionParams: any) => void,
  ) {
    const isAddingQuestion = Object.keys(currentQuestion).length === 0;
    const personAssociationId = getPersonAssociation(data, associatedPersons);
    const questionDetail = {
      ExternalDocumentUri: data?.ExternalDocumentUri || '',
      HelpText: data?.HelpText || '',
      ProcedureUri: data?.ProcedureUri || '',
      Type: data?.QuestionDetailType,
      Id: selectedQuestionType?.QuestionDetail?.Id || 0,
    };
    const questionTemplate = {
      ...selectedQuestionType,
    };
    delete questionTemplate.QuestionDetail;

    const questionTypeOption = getQuestionTypeDetails(
      data,
      selectedQuestionType,
      mappedColumns,
      lookupDependency,
    );

    const questionOptions = getQuestionOptions(
      selectedQuestionType,
      questionTemplateOptions,
    );

    const questionParams = {
      AccountTag: '',
      FieldLabel: data?.UpdatedFieldLabel,
      Id:
        selectedQuestionType && isAddingQuestion ? 0 : selectedQuestionType?.Id, // shuld be updated for edit
      IsDeleted: false,
      IsMultiValuesAllowed: !!data.IsMultiValuesAllowed,
      PersonAssociation: personAssociationId === 2 ? null : personAssociationId,
      QuestionDetail: questionDetail,
      QuestionOptions: questionOptions,
      QuestionTemplate: { ...questionTemplate },
      QuestionTemplateId: selectedQuestionType?.QuestionTemplateId,
      QuestionTypeOption: isAddingQuestion
        ? { ...questionTypeOption, Id: 0 }
        : questionTypeOption,
      Order: selectedQuestionType?.Order
        ? selectedQuestionType?.Order
        : undefined,
      SectionId: currentSection?.Id,
      IsFlagged: data?.IsFlagged || false,
    };

    const itemLabels = questionParams.QuestionOptions.map(item =>
      item.ItemLabel.toLowerCase().replace(/ /g, ''),
    );

    const duplicateLabels = itemLabels.filter(
      (item, index) => itemLabels.indexOf(item) !== index,
    );

    if (data?.QuestionDetailType === 1 && data?.ProcedureUri === '') {
      showErrorToast({
        message: 'Please select a procedure in question details.',
        toastId: 10245,
      });
    } else if (
      data?.QuestionDetailType === 2 &&
      (data?.ExternalDocumentUri === '' ||
        data?.ExternalDocumentUri === undefined)
    ) {
      showErrorToast({
        message: 'Please select an external document in question details.',
      });
    } else if (duplicateLabels.length !== 0) {
      showErrorToast({
        message: 'Item label already exists',
      });
    } else {
      handleAddEditQuestion(questionParams, currentSection, cb);
      setQuestionTemplateOptions([]);
    }
  }

  const validateLimitedAccess = (ids: number[]): Promise<any> => {
    return new Promise((resolve, reject) => {
      mutation.mutate(ids, {
        onSuccess: resolve,
        onError: reject,
      });
    });
  };

  const questionSubmit = handleSubmit(async (data: any) => {
    try {
      let defaultUrl = '';
      if (url.includes('http://')) {
        defaultUrl = url.replace('http://', '').trim();
      } else if (url.includes('https://')) {
        defaultUrl = url.replace('https://', '').trim();
      }
      let value: any;
      if (
        selectedQuestionType.QuestionTypeOption.$type ===
        'ChoiceQuestionTypeOption'
      ) {
        if (checkKeyForNumericString(questionTemplateOptions).includes(false)) {
          showErrorToast({
            message: 'Order should be an integer value',
          });
          return;
        }
        if (checkifEmptyItemName(questionTemplateOptions).length) {
          showErrorToast({
            message: 'Item Name is mandatory',
          });
          return;
        }
        // Check limited access linked options
        if (currentQuestion.QuestionOptions?.length > 0) {
          const existingIsDefaultOptionIds =
            currentQuestion.QuestionOptions.filter(
              (item: any) => item.Id !== 0,
            ).map((item: { Id: number }) => item.Id);

          if (existingIsDefaultOptionIds.length > 0) {
            const currentIsDefaultOptionIds = questionTemplateOptions
              .filter(x => x?.IncludeByDefault)
              .map(y => y.Id);
      
            const removedOptionIds = existingIsDefaultOptionIds.filter(
              (id: number) => !currentIsDefaultOptionIds.includes(id),
            );

            // if Option removed, validate with API
            if (removedOptionIds.length > 0) {
              const response = await validateLimitedAccess(removedOptionIds);

              if (response.Data?.length > 0) {
                confirmAlert({
                  message: `This record is associated with an active user. Choice options linked to user limited access cannot be excluded.`,
                  buttons: [
                    {
                      label: 'Ok',
                      onClick: () => {
                        const linkedIds = new Set(response.Data);

                        const updatedQuestionTemplateOptions =
                          questionTemplateOptions.map(option => ({
                            ...option,
                            IncludeByDefault: linkedIds.has(option.Id)
                              ? true
                              : option.IncludeByDefault,
                          }));

                        setQuestionTemplateOptions(
                          updatedQuestionTemplateOptions,
                        );

                        return;
                      },
                      className: 'pr-btn-primary',
                    },
                  ],
                });

                return;
              }
            }
          }
        }
      }
      if (data?.TextQuestionTypeFormatId === 7) {
        value = {
          ...data,
          DefaultValue: defaultUrl.length ? url : '',
        };
      } else {
        value = {
          ...data,
        };
      }
      if (data?.TextQuestionTypeFormatId === 7 && defaultUrl.length) {
        setSubmit(true);
        geUrlValidation().then((response: any) => {
          if (response?.data?.Data.StatusCode === 200) {
            onSubmit(value);
            setLookupDependency([]);
          }
        });
      } else {
        onSubmit(value);
        setLookupDependency([]);
      }
    } catch (err) {
      showErrorToast({
        message: 'Unable to submit the question. Please try again.',
      });
    }
  });

  function handleFormandQuestionSave() {
    handleSubmit((data: any) => {
      onSubmit(
        data,
        (newSections: Array<SectionTypeInput>, questionParams: any) => {
          handleSaveForm(newSections, questionParams);
        },
      );
    })();
  }

  const addQuestionActions = () => (
    <>
      <Button
        variant="secondary"
        onClick={() => {
          togglePanel();
          resetForm();
        }}
      >
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={questionSubmit}
        disabled={
          (!selectedQuestionType?.Id &&
            Object.keys(currentQuestion)?.length === 0) ||
          isLookupFetching
        }
      >
        Save
      </Button>
    </>
  );

  const AddQuestionTabs = [
    {
      id: 'questionDefinition',
      label: 'Question Definition',
      content: (
        <QuestionDefinition
          showPanel={showPanel}
          setQuestionType={setQuestionType}
          selectedQuestionType={selectedQuestionType}
          resetForm={resetForm}
          currentQuestion={currentQuestion}
          associatedPersons={associatedPersons}
          register={register}
          control={control}
          Controller={Controller}
          watch={watch}
          setValue={setValue}
          errors={errors}
          getValues={getValues}
          reset={reset}
          accountId={accountId}
          mappedColumns={mappedColumns}
          setMappedColumns={setMappedColumns}
          questionTemplateOptions={questionTemplateOptions}
          setQuestionTemplateOptions={setQuestionTemplateOptions}
          sections={sections}
          setLookupDependency={setLookupDependency}
          lookupDependency={lookupDependency}
          handleFormandQuestionSave={handleFormandQuestionSave}
          url={url}
          setUrl={setUrl}
          setIsLookupFetching={setIsLookupFetching}
          errorMessage={errorMessage}
        />
      ),
    },
    {
      id: 'questionDetails',
      label: 'Question Details',
      content: (
        <QuestionDetails
          selectedQuestionType={selectedQuestionType}
          control={control}
          Controller={Controller}
          watch={watch}
          setValue={setValue}
          accountId={accountId}
          accountInfoReturn={accountInfoReturn}
        />
      ),
    },
  ];

  return (
    <>
      <SlidePanel
        title={
          Object.keys(currentQuestion).length === 0
            ? 'Add New Question'
            : 'Edit Question'
        }
        open={showPanel}
        onClose={() => {
          togglePanel();
          resetForm();
        }}
        actions={addQuestionActions()}
        size="lg"
      >
        {showPanel && <Tabs tabs={AddQuestionTabs} />}
      </SlidePanel>
    </>
  );
}
